
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function ValDeVieEstatePage() {
  useEffect(() => {
    document.title = 'Val De Vie Estate Painters Paarl | Luxury Wine Estate Painting | Premium Property Services';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services for Val De Vie Estate, Paarl. Expert luxury wine estate painting, premium residential properties, and exclusive estate services. Serving Val De Vie and surrounding wine estates.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Val De Vie Estate painters, luxury wine estate painting, premium property Paarl');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/val-de-vie-estate`);
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Val De Vie Estate Painters Paarl",
      "description": "Professional painting services for Val De Vie Estate, Paarl",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/val-de-vie-estate`,
      "areaServed": {
        "@type": "Place",
        "name": "Val De Vie Estate, Paarl, Western Cape, South Africa"
      },
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": -33.7123,
          "longitude": 18.9456
        },
        "geoRadius": "15000"
      }
    });
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Val De Vie Estate Painters')) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/3150ac70536b57dd699ec0398cdd00b2.jpg"
            alt="Professional painting services for Val De Vie Estate, Paarl"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Exclusive <span className="text-yellow-400">Val De Vie Estate Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Premium painting services for Val De Vie Estate's luxury properties. Specialized estate painting with uncompromising quality and discretion for discerning wine estate residents.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-500 transition-colors text-center whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </Link>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Exclusive Estate Painting Services at Val De Vie
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Bespoke painting solutions for Val De Vie Estate's luxury properties, from exclusive residences to estate facilities, with uncompromising attention to detail and discretion.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-home-smile-2-line',
                title: 'Luxury Estate Homes',
                description: 'Premium painting services for exclusive Val De Vie residences with vineyard and polo field views, using the finest materials and finishes.',
                features: ['Luxury residences', 'Vineyard view homes', 'Premium finishes', 'Exclusive service']
              },
              {
                icon: 'ri-building-2-line',
                title: 'Estate Facilities',
                description: 'Professional painting for estate amenities including clubhouses, wine cellars, and recreational facilities.',
                features: ['Clubhouse facilities', 'Wine cellars', 'Recreational areas', 'Estate amenities']
              },
              {
                icon: 'ri-vip-crown-line',
                title: 'Concierge Painting',
                description: 'White-glove painting service with complete discretion, flexible scheduling, and minimal disruption to estate lifestyle.',
                features: ['Concierge service', 'Flexible scheduling', 'Complete discretion', 'Minimal disruption']
              }
            ].map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                      <i className={`${feature.icon} text-black text-2xl`}></i>
                    </div>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-8">
                Why Val De Vie Estate Residents Choose Our Exclusive Services
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Luxury Estate Expertise',
                    description: 'Specialized experience with high-end wine estate properties and understanding of luxury lifestyle requirements.'
                  },
                  {
                    title: 'Uncompromising Quality',
                    description: 'Premium materials and master craftsmen ensuring the finest finishes worthy of Val De Vie\'s prestigious reputation.'
                  },
                  {
                    title: 'Complete Discretion',
                    description: 'Professional, discrete service respecting the privacy and exclusivity expected by estate residents.'
                  },
                  {
                    title: 'Estate Lifestyle Understanding',
                    description: 'Flexible scheduling around polo events, wine tastings, and estate activities with minimal lifestyle disruption.'
                  }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-black text-sm"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-black mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20Val%20De%20Vie%20Estate%20luxury%20property%2C%20exclusive%20wine%20estate%20home%20maintenance%2C%20premium%20painting%20services%20with%20vineyard%20backdrop%2C%20high-end%20residential%20painting%20Paarl&width=600&height=700&seq=val-de-vie-team-work&orientation=portrait"
                alt="Exclusive estate painting at Val De Vie"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Recent Val De Vie Estate Luxury Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcase of our latest exclusive painting projects at Val De Vie Estate, demonstrating our commitment to luxury and excellence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Vineyard Estate Mansion',
                location: 'Val De Vie Vineyard Views',
                description: 'Complete luxury renovation of a 6-bedroom estate home with panoramic vineyard and mountain views.',
                image: 'https://readdy.ai/api/search-image?query=Vineyard%20estate%20mansion%20painting%20at%20Val%20De%20Vie%2C%20luxury%20home%20with%20vineyard%20views%2C%20high-end%20residential%20architecture%2C%20premium%20estate%20property%20painting%20Paarl&width=400&height=300&seq=val-de-vie-project-1&orientation=landscape'
              },
              {
                title: 'Estate Clubhouse Renovation',
                location: 'Val De Vie Central',
                description: 'Prestigious clubhouse painting renovation including dining areas, lounges, and recreational facilities.',
                image: 'https://readdy.ai/api/search-image?query=Estate%20clubhouse%20renovation%20painting%20at%20Val%20De%20Vie%2C%20luxury%20clubhouse%20facilities%2C%20premium%20hospitality%20painting%2C%20exclusive%20estate%20amenities%20Paarl&width=400&height=300&seq=val-de-vie-project-2&orientation=landscape'
              },
              {
                title: 'Polo Field Pavilion',
                location: 'Val De Vie Polo Fields',
                description: 'Elegant painting of the polo pavilion and spectator facilities maintaining estate sophistication.',
                image: 'https://readdy.ai/api/search-image?query=Polo%20field%20pavilion%20painting%20at%20Val%20De%20Vie%20Estate%2C%20luxury%20sports%20facility%2C%20elegant%20pavilion%20architecture%2C%20exclusive%20polo%20club%20painting%20Paarl&width=400&height=300&seq=val-de-vie-project-3&orientation=landscape'
              }
            ].map((project, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{project.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              What Val De Vie Estate Residents Say
            </h2>
            <p className="text-xl text-gray-600">
              Testimonials from satisfied luxury property owners at Val De Vie Estate
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Charles Wellington',
                location: 'Val De Vie Vineyard Views',
                rating: 5,
                text: 'Exceptional service for our estate home. The quality and attention to detail were impeccable, and they worked around our schedule with complete discretion.'
              },
              {
                name: 'Victoria Ashworth',
                location: 'Val De Vie Central',
                rating: 5,
                text: 'Outstanding work on our clubhouse renovation. They understood the prestige of Val De Vie and delivered results that exceed our high expectations.'
              },
              {
                name: 'Alexander Pemberton',
                location: 'Val De Vie Polo Fields',
                rating: 5,
                text: 'Professional and discrete service for our polo pavilion. The quality of workmanship and respect for our estate lifestyle was exemplary.'
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-semibold text-black">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Elevate Your Val De Vie Estate Property
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us for exclusive estate painting consultation and luxury services. Experience the finest in wine estate painting craftsmanship and discretion.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
